import React, { useEffect, useRef, useState } from 'react';
import globalImageCache from './globalImageCache';
import '../../styles/contents/shortFormQuiz/ShortFormQuizBalloonPop.css';

// 피드백 이미지들 (public 폴더 경로)
const bubbleSuccessUrl = '/assets/images/contents/shortForm/bubble/Bubble_Success.png';
const bubbleFailUrl = '/assets/images/contents/shortForm/bubble/Bubble_Fail.png';

// 풍선 이미지들 (public 폴더 경로)
const skyBalloonUrl = '/assets/images/contents/shortForm/SkyblueBalloon.png';
const yellowBalloonUrl = '/assets/images/contents/shortForm/YellowBalloon.png';
const purpleBalloonUrl = '/assets/images/contents/shortForm/PuppleBalloon.png';
const pinkBalloonUrl = '/assets/images/contents/shortForm/PinkBalloon.png';
const slingshotUrl = '/assets/images/contents/shortForm/Slingshot.png';
const crosshairUrl = '/assets/images/contents/shortForm/Crosshair.png';

const ShortFormQuizBalloonPop = ({ 
  stagePhase, 
  localStage, 
  quiz, 
  setStagePhase, 
  correctCountRef, 
  totalQuestionsRef, 
  answersRef, 
  wrongIndexesRef, 
  stageAnsweredRef,
  feedbackActive,
  feedbackIsSuccess,
  feedbackRect,
  setFeedbackActive,
  setFeedbackIsSuccess,
  setFeedbackRect,
  setIsGameInProgress,
  successImageUrl,
  failImageUrl,
  // 새로 추가된 props
  includeTutorial,
  isTutorialStage
}) => {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const [balloons, setBalloons] = useState([]);
  const balloonsRef = useRef([]);
  const [projectiles, setProjectiles] = useState([]);
  const projectilesRef = useRef([]);
  const [slingshot, setSlingshot] = useState({ x: 0, y: 0, isDragging: false, dragStartX: 0, dragStartY: 0, pullDistance: 0 });
  const slingshotRef = useRef({ x: 0, y: 0, isDragging: false, dragStartX: 0, dragStartY: 0, pullDistance: 0 });
  const [crosshair, setCrosshair] = useState({ x: 0, y: 0, visible: false });
  const crosshairRef = useRef({ x: 0, y: 0, visible: false });
  const [trajectoryDots, setTrajectoryDots] = useState([]);
  const trajectoryDotsRef = useRef([]);

  const imagesRef = useRef({ 
    skyBalloon: null, 
    yellowBalloon: null, 
    purpleBalloon: null, 
    pinkBalloon: null, 
    slingshot: null, 
    crosshair: null,
    success: null,
    fail: null,
    ready: false 
  });
  const [optionMeta, setOptionMeta] = useState({ type: 'tutorial', correctIndex: 0, selectedIndex: null });

  // 2D 게임으로 간소화 - 3D 계산 제거

  // 텍스트 줄바꿈 처리 함수
  const wrapText = (ctx, text, maxWidth) => {
    const words = text.split(' ');
    const lines = [];
    let currentLine = words[0];

    for (let i = 1; i < words.length; i++) {
      const word = words[i];
      const width = ctx.measureText(currentLine + ' ' + word).width;
      if (width < maxWidth) {
        currentLine += ' ' + word;
      } else {
        lines.push(currentLine);
        currentLine = word;
      }
    }
    lines.push(currentLine);
    return lines;
  };

  // 간단한 직선 물리 시스템
  const PHYSICS_CONFIG = {
    timeStep: 0.016,        // 60fps 기준 시간 간격 (16ms)
    trajectoryPointCount: 10, // 궤적 점 개수
    projectileSpeed: 8      // 발사체 이동 속도 (픽셀/프레임)
  };
  
  // 간단한 직선 발사 계산 함수
  const calculateLinearLaunch = (mouseX, mouseY, slingshotData, canvasWidth) => {
    const maxPullDistance = Math.max(300, Math.min(500, canvasWidth * 0.5));
    const dx = mouseX - slingshotData.x;
    const dy = mouseY - slingshotData.y;
    const pullDistance = Math.hypot(dx, dy);
    
    // 아래쪽으로만 당길 수 있도록 제한
    if (dy <= 0) {
      return null;
    }
    
    // 당기기 거리 제한
    const limitedPullDistance = Math.min(pullDistance, maxPullDistance);
    const pullStrength = limitedPullDistance / maxPullDistance;
    
    return { pullStrength };
  };

  // 전역 캐시에서 이미지 가져오기
  useEffect(() => {
    if (globalImageCache.isReadyToUse()) {
      // 전역 캐시에서 이미지 가져오기
      const skyBalloon = globalImageCache.get(skyBalloonUrl);
      const yellowBalloon = globalImageCache.get(yellowBalloonUrl);
      const purpleBalloon = globalImageCache.get(purpleBalloonUrl);
      const pinkBalloon = globalImageCache.get(pinkBalloonUrl);
      const slingshot = globalImageCache.get(slingshotUrl);
      const crosshair = globalImageCache.get(crosshairUrl);
      const success = globalImageCache.get(bubbleSuccessUrl);
      const fail = globalImageCache.get(bubbleFailUrl);
      
      imagesRef.current = { 
        skyBalloon,
        yellowBalloon,
        purpleBalloon,
        pinkBalloon,
        slingshot,
        crosshair,
        success,
        fail,
        ready: true 
      };
      
    } else {
      // 전역 캐시가 준비되지 않았으면 기존 방식으로 로딩
      
      // 이미지 객체들 직접 생성 (fallback)
      const skyBalloon = new Image(); skyBalloon.src = skyBalloonUrl;
      const yellowBalloon = new Image(); yellowBalloon.src = yellowBalloonUrl;
      const purpleBalloon = new Image(); purpleBalloon.src = purpleBalloonUrl;
      const pinkBalloon = new Image(); pinkBalloon.src = pinkBalloonUrl;
      const slingshot = new Image(); slingshot.src = slingshotUrl;
      const crosshair = new Image(); crosshair.src = crosshairUrl;
      const success = new Image(); success.src = bubbleSuccessUrl;
      const fail = new Image(); fail.src = bubbleFailUrl;

      let loaded = 0;
      const totalImages = 8;
      
      const onLoaded = () => { 
        loaded += 1; 
        if (loaded >= totalImages) { 
          imagesRef.current.ready = true;
        } 
      };
      
      const onErr = (src) => { 
        console.error(`풍선 터트리기 이미지 로딩 실패: ${src}`); 
        onLoaded(); // 에러도 완료로 카운트
      };
      
      [skyBalloon, yellowBalloon, purpleBalloon, pinkBalloon, slingshot, crosshair, success, fail].forEach((img) => { 
        img.onload = onLoaded; 
        img.onerror = () => onErr(img.src); 
      });
      
      imagesRef.current = { 
        skyBalloon,
        yellowBalloon,
        purpleBalloon,
        pinkBalloon,
        slingshot,
        crosshair,
        success,
        fail,
        ready: false 
      };

      // 클린업
      return () => {
        [skyBalloon, yellowBalloon, purpleBalloon, pinkBalloon, slingshot, crosshair, success, fail].forEach((img) => { 
          img.onload = null; 
          img.onerror = null; 
        });
      };
    }
  }, [globalImageCache]);

  // 게임 초기화 및 렌더링
  useEffect(() => {
    if (stagePhase !== 'pre' && stagePhase !== 'play' && stagePhase !== 'feedback') {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const parent = canvas.parentElement;
    if (!parent) return;
    
    // StudentPage의 레이아웃에 맞는 크기 계산
    // 상단 UI 영역(20%)을 제외한 나머지 80% 영역을 완전히 사용
    const parentRect = parent.getBoundingClientRect();
    const availableWidth = parentRect.width;
    const availableHeight = parentRect.height * 0.8; // 상단 UI 20% 제외
    
    // 사용 가능한 영역을 완전히 채우도록 설정
    const width = availableWidth;
    const height = availableHeight;
    
    // 캔버스 크기 설정
    if (stagePhase === 'pre' || stagePhase === 'play') {
      canvas.width = Math.round(width);
      canvas.height = Math.round(height);
    }

    // 새총 위치 설정 (중앙 하단) - 화면 크기에 비례하여 조정
    const slingshotSize = Math.max(200, Math.min(340, width * 0.27));
    const slingshotX = width / 2;
    const slingshotY = height * 0.75; // 화면 하단 75% 위치
    setSlingshot({ x: slingshotX, y: slingshotY, isDragging: false, dragStartX: 0, dragStartY: 0, pullDistance: 0 });
    slingshotRef.current = { x: slingshotX, y: slingshotY, isDragging: false, dragStartX: 0, dragStartY: 0, pullDistance: 0 };
    
    // 조준점 및 궤적 초기화
    setCrosshair({ x: 0, y: 0, visible: false });
    crosshairRef.current = { x: 0, y: 0, visible: false };
    setTrajectoryDots([]);
    trajectoryDotsRef.current = [];

    // 게임 데이터 초기화 (feedback 단계에서는 초기화 금지)
    if ((stagePhase === 'pre' || stagePhase === 'play')) {
      if (isTutorialStage) {
        // 튜토리얼 단계: 단일 풍선 - 화면 크기에 비례하여 조정
        const balloonSize = Math.max(180, Math.min(270, width * 0.27));
        const balloonX = width / 2;
        const balloonY = height * 0.45; // 타이머 UI와 겹치지 않도록 배치
        
        setBalloons([{
          id: 'tutorial-balloon',
          type: 'single',
          x: balloonX,
          y: balloonY,
          size: balloonSize,
          color: 'sky',
          popped: false,
          active: true
        }]);
        
        balloonsRef.current = [{
          id: 'tutorial-balloon',
          type: 'single',
          x: balloonX,
          y: balloonY,
          size: balloonSize,
          color: 'sky',
          popped: false,
          active: true
        }];
        
        setOptionMeta({ type: 'tutorial' });
      } else {
        // 퀴즈 단계: 퀴즈 풍선들 - 화면 크기에 비례하여 조정
        const balloonSize = Math.max(135, Math.min(225, width * 0.225));
        const colors = ['sky', 'yellow', 'purple', 'pink'];
        
        // 퀴즈 데이터 로드 - 수정된 인덱스 계산
        const qList = Array.isArray(quiz?.questions) ? quiz.questions : [];
        let qIndexRaw;
        
        if (includeTutorial) {
          // 튜토리얼 포함: localStage 5 = qIndex 0
          qIndexRaw = localStage - 5;
        } else {
          // 튜토리얼 없음: localStage 1 = qIndex 0
          qIndexRaw = localStage - 1;
        }
        
        let options = ['선택지 A', '선택지 B', '선택지 C', '선택지 D'];
        let correctIndex = 0;
        
        // 유효한 문제 범위 내에서만 처리
        if (qList.length > 0 && qIndexRaw >= 0 && qIndexRaw < qList.length) {
          const q = qList[qIndexRaw];
          if (q && Array.isArray(q.options)) {
            const arr = q.options.slice(0, 4);
            while (arr.length < 4) arr.push(`선택지 ${String.fromCharCode(65 + arr.length)}`);
            options = arr.map(v => (typeof v === 'string' ? v : String(v)));
          }
          if (typeof q?.answerIndex === 'number' && q.answerIndex >= 0 && q.answerIndex < 4) {
            correctIndex = q.answerIndex;
          }
        }
        
        // 풍선들을 상단에 배치 - 화면 비율에 맞게 조정
        const balloonData = [];
        const spacing = width / 4; // 간격 조정
        const startX = spacing * 0.5; // 시작 위치 조정
        const balloonY = height * 0.4; // 타이머 UI와 겹치지 않도록 배치
        
        for (let i = 0; i < 4; i++) {
          balloonData.push({
            id: `quiz-balloon-${i}`,
            type: 'quiz',
            x: startX + i * spacing,
            y: balloonY,
            size: balloonSize,
            color: colors[i],
            popped: false,
            optionIndex: i,
            text: options[i],
            correct: i === correctIndex
          });
        }
        
        setBalloons(balloonData);
        balloonsRef.current = balloonData;
        setOptionMeta({ type: 'quiz', correctIndex, selectedIndex: null });
      }
      
      // 발사체 초기화
      setProjectiles([]);
      projectilesRef.current = [];
    }

    // 렌더링 루프
    const ctx = canvas.getContext('2d');
    
    const draw = () => {
      // 배경을 투명하게 설정 (StudentPage의 GameScreenLayout 배경색 사용)
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // 풍선 그리기 (2D 단순화)
      const balloonArray = balloonsRef.current || [];
      
      balloonArray.forEach((balloon) => {
        if (balloon.popped) return;
        
        let balloonImg = null;
        switch (balloon.color) {
          case 'sky': balloonImg = imagesRef.current.skyBalloon; break;
          case 'yellow': balloonImg = imagesRef.current.yellowBalloon; break;
          case 'purple': balloonImg = imagesRef.current.purpleBalloon; break;
          case 'pink': balloonImg = imagesRef.current.pinkBalloon; break;
        }
        
        if (balloonImg && balloonImg.complete && imagesRef.current.ready) {
          ctx.drawImage(
            balloonImg, 
            balloon.x - balloon.size / 2, 
            balloon.y - balloon.size / 2, 
            balloon.size, 
            balloon.size
          );
          
          // 퀴즈 텍스트 오버레이 (줄바꿈 처리)
          if (balloon.type === 'quiz' && balloon.text) {
            ctx.fillStyle = '#ffffff';
            const fontSize = Math.max(12, Math.round(balloon.size * 0.15));
            ctx.font = `bold ${fontSize}px Arial`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.strokeStyle = '#000000';
            ctx.lineWidth = 2;
            
            // 텍스트 줄바꿈 처리
            const maxWidth = balloon.size * 0.8; // 풍선 크기의 80%
            const lines = wrapText(ctx, balloon.text, maxWidth);
            const lineHeight = fontSize * 1.2;
            const totalHeight = lines.length * lineHeight;
            const startY = balloon.y - totalHeight / 2 + lineHeight / 2;
            
            lines.forEach((line, index) => {
              const lineY = startY + index * lineHeight;
              ctx.strokeText(line, balloon.x, lineY);
              ctx.fillText(line, balloon.x, lineY);
            });
          }
        }
      });

      // 발사체 및 트레일 그리기 (2D 단순화)
      const projectileArray = projectilesRef.current || [];
      projectileArray.forEach((projectile) => {
        // 트레일 그리기
        if (projectile.trail && projectile.trail.length > 1) {
          ctx.strokeStyle = '#FF6B35';
          ctx.lineWidth = 3;
          ctx.globalAlpha = 0.6;
          
          ctx.beginPath();
          for (let i = 0; i < projectile.trail.length; i++) {
            const trailPoint = projectile.trail[i];
            
            if (i === 0) {
              ctx.moveTo(trailPoint.x, trailPoint.y);
            } else {
              ctx.lineTo(trailPoint.x, trailPoint.y);
            }
          }
          ctx.stroke();
          ctx.globalAlpha = 1;
        }
        
        // 발사체 그리기
        ctx.beginPath();
        ctx.arc(projectile.x, projectile.y, projectile.radius, 0, Math.PI * 2);
        ctx.fillStyle = '#8B4513';
        ctx.fill();
        ctx.strokeStyle = '#654321';
        ctx.lineWidth = 2;
        ctx.stroke();
      });

      // 새총 그리기 - 동적 크기 사용 (50% 증가 = 1.5배)
      const slingshotData = slingshotRef.current;
      const slingshotImg = imagesRef.current.slingshot;
      if (slingshotImg && slingshotImg.complete && imagesRef.current.ready) {
        const slingshotSize = Math.max(200, Math.min(340, canvas.width * 0.27));
        ctx.drawImage(
          slingshotImg,
          slingshotData.x - slingshotSize / 2,
          slingshotData.y - slingshotSize / 2,
          slingshotSize,
          slingshotSize
        );
        
        // 발사 전 구슬 표시 (새총에 장전된 상태) - 발사체가 없을 때만 표시
        const currentProjectiles = projectilesRef.current || [];
        if (currentProjectiles.length === 0) {
          const marbleRadius = Math.max(8, Math.min(15, canvas.width * 0.015));
          const marbleX = slingshotData.x;
          const marbleY = slingshotData.y - slingshotSize * 0.1; // 새총 중앙보다 약간 위
          
          // 구슬 그리기
          ctx.beginPath();
          ctx.arc(marbleX, marbleY, marbleRadius, 0, Math.PI * 2);
          ctx.fillStyle = '#8B4513';
          ctx.fill();
          ctx.strokeStyle = '#654321';
          ctx.lineWidth = 2;
          ctx.stroke();
          
          // 구슬 하이라이트
          ctx.beginPath();
          ctx.arc(marbleX - marbleRadius * 0.3, marbleY - marbleRadius * 0.3, marbleRadius * 0.3, 0, Math.PI * 2);
          ctx.fillStyle = '#D2B48C';
          ctx.fill();
        }
      }
      
      // 예상 궤적 점들 그리기
      const trajectoryData = trajectoryDotsRef.current || [];
      if (trajectoryData.length > 0) {
        trajectoryData.forEach((point, index) => {
          const dotRadius = 3;
          const opacity = Math.max(0.3, 1 - (index / trajectoryData.length) * 0.7); // 점점 투명해짐
          
          ctx.globalAlpha = opacity;
          ctx.beginPath();
          ctx.arc(point.x, point.y, dotRadius, 0, Math.PI * 2);
          ctx.fillStyle = '#FFFFFF';
          ctx.fill();
          ctx.strokeStyle = '#CCCCCC';
          ctx.lineWidth = 1;
          ctx.stroke();
        });
        ctx.globalAlpha = 1; // 투명도 리셋
      }

      // 조준점 그리기 - 동적 크기 사용
      const crosshairData = crosshairRef.current;
      const crosshairImg = imagesRef.current.crosshair;
      if (crosshairData.visible && crosshairImg && crosshairImg.complete && imagesRef.current.ready) {
        const crosshairSize = Math.max(30, Math.min(50, canvas.width * 0.05));
        ctx.drawImage(
          crosshairImg,
          crosshairData.x - crosshairSize / 2,
          crosshairData.y - crosshairSize / 2,
          crosshairSize,
          crosshairSize
        );
      }

      // 피드백 오버레이 그리기
      if (stagePhase === 'feedback' && feedbackActive && imagesRef.current.ready) {
        ctx.fillStyle = 'rgba(107, 114, 128, 0.5)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        const img = feedbackIsSuccess ? imagesRef.current.success : imagesRef.current.fail;
        if (img) {
          const size = Math.min(canvas.width, canvas.height) * 0.3;
          const cx = canvas.width / 2;
          const cy = canvas.height / 2;
          ctx.drawImage(img, cx - size / 2, cy - size / 2, size, size);
        }
      }

      rafRef.current = requestAnimationFrame(draw);
    };
    
    draw();

    // 마우스/터치 이벤트 처리 - 개선된 터치 지원
    const resolvePoint = (e) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      
      // 터치 이벤트 처리 (우선순위)
      if (e.touches && e.touches.length > 0) {
        const touch = e.touches[0];
        return {
          x: (touch.clientX - rect.left) * scaleX,
          y: (touch.clientY - rect.top) * scaleY,
          isTouch: true
        };
      }
      
      // 터치 종료 이벤트 처리 (changedTouches)
      if (e.changedTouches && e.changedTouches.length > 0) {
        const touch = e.changedTouches[0];
        return {
          x: (touch.clientX - rect.left) * scaleX,
          y: (touch.clientY - rect.top) * scaleY,
          isTouch: true
        };
      }
      
      // 마우스 이벤트 처리
      const clientX = e.clientX ?? (e.pageX ?? 0);
      const clientY = e.clientY ?? (e.pageY ?? 0);
      return { 
        x: (clientX - rect.left) * scaleX, 
        y: (clientY - rect.top) * scaleY, 
        isTouch: e.pointerType === 'touch' || e.type.includes('touch')
      };
    };

    // 간단한 직선 궤적 계산 함수
    const calculateLinearTrajectory = (startX, startY, targetX, targetY) => {
      const points = [];
      const dx = targetX - startX;
      const dy = targetY - startY;
      const distance = Math.hypot(dx, dy);
      
      // 정규화된 방향 벡터
      const dirX = dx / distance;
      const dirY = dy / distance;
      
      // 궤적 점들 생성 (직선상의 균등한 간격)
      for (let i = 1; i <= PHYSICS_CONFIG.trajectoryPointCount; i++) {
        const ratio = i / PHYSICS_CONFIG.trajectoryPointCount;
        const x = startX + dirX * distance * ratio;
        const y = startY + dirY * distance * ratio;
        points.push({ x, y });
      }
      
      return points;
    };
    
    // 간단한 직선 조준점 및 궤적 계산 함수
    const calculateCrosshairAndTrajectory = (currentX, currentY, slingshotData) => {
      // 간단한 발사 계산 사용
      const physics = calculateLinearLaunch(currentX, currentY, slingshotData, canvas.width);
      
      if (!physics) {
        return { crosshair: { x: slingshotData.x, y: slingshotData.y }, trajectory: [] };
      }
      
      const { pullStrength } = physics;
      
      // 새총에서 마우스 위치로의 벡터 계산
      const dx = currentX - slingshotData.x;
      const dy = currentY - slingshotData.y;
      const distance = Math.hypot(dx, dy);
      
      // 정규화된 방향 벡터 (새총에서 마우스 반대 방향)
      const dirX = -dx / distance;
      const dirY = -dy / distance;
      
      // 조준점까지의 거리 (당기는 강도에 따라)
      const maxRange = Math.min(canvas.width, canvas.height) * 0.8;
      const aimDistance = pullStrength * maxRange;
      
      // 조준점 위치 계산
      const crosshairX = slingshotData.x + dirX * aimDistance;
      const crosshairY = slingshotData.y + dirY * aimDistance;
      
      // 직선 궤적 계산
      const trajectory = calculateLinearTrajectory(
        slingshotData.x, 
        slingshotData.y, 
        crosshairX, 
        crosshairY
      );
      
      return { 
        crosshair: { x: crosshairX, y: crosshairY }, 
        trajectory: trajectory
      };
    };

    const handleMouseDown = (evt) => {
      if (evt && evt.cancelable) evt.preventDefault();
      const { x: mouseX, y: mouseY, isTouch } = resolvePoint(evt);
      const slingshotData = slingshotRef.current;
      
      // 새총 근처에서 드래그 시작하는지 확인 - 터치는 더 넓은 범위
      const baseRadius = Math.max(135, Math.min(200, canvas.width * 0.20));
      const clickRadius = isTouch ? baseRadius * 1.2 : baseRadius; // 터치는 20% 더 넓게
      const distance = Math.hypot(mouseX - slingshotData.x, mouseY - slingshotData.y);
      
      if (distance <= clickRadius) {
        slingshotRef.current = { 
          ...slingshotData, 
          isDragging: true, 
          dragStartX: mouseX, 
          dragStartY: mouseY 
        };
        setSlingshot(slingshotRef.current);
        
        // 조준점 및 궤적 표시
        const result = calculateCrosshairAndTrajectory(mouseX, mouseY, slingshotData);
        crosshairRef.current = { ...result.crosshair, visible: true };
        setCrosshair(crosshairRef.current);
        trajectoryDotsRef.current = result.trajectory;
        setTrajectoryDots(result.trajectory);
        
      } else {
      }
    };

    const handleMouseMove = (evt) => {
      if (evt && evt.cancelable) evt.preventDefault();
      const slingshotData = slingshotRef.current;
      
      if (slingshotData.isDragging) {
        const { x: mouseX, y: mouseY } = resolvePoint(evt);
        
        // 조준점 및 궤적 업데이트
        const result = calculateCrosshairAndTrajectory(mouseX, mouseY, slingshotData);
        crosshairRef.current = { ...result.crosshair, visible: true };
        setCrosshair(crosshairRef.current);
        trajectoryDotsRef.current = result.trajectory;
        setTrajectoryDots(result.trajectory);
      }
    };

    const handleMouseUp = (evt) => {
      if (evt && evt.cancelable) evt.preventDefault();
      const slingshotData = slingshotRef.current;
      
      if (slingshotData.isDragging) {
        const { x: mouseX, y: mouseY } = resolvePoint(evt);
        
        // 간단한 발사 계산 사용
        const physics = calculateLinearLaunch(mouseX, mouseY, slingshotData, canvas.width);
        
        // 터치와 마우스 모두를 위한 관대한 발사 조건
        const pullDistance = Math.hypot(mouseX - slingshotData.x, mouseY - slingshotData.y);
        const minPullDistance = 5; // 최소 당기기 거리를 줄임 (10 -> 5)
        
        if (physics && pullDistance > minPullDistance) {
          // 조준점까지의 직선 방향과 거리 계산
          const targetX = crosshairRef.current.x;
          const targetY = crosshairRef.current.y;
          const dx = targetX - slingshotData.x;
          const dy = targetY - slingshotData.y;
          const distance = Math.hypot(dx, dy);
          
          // 정규화된 방향 벡터와 속도
          const dirX = dx / distance;
          const dirY = dy / distance;
          const speed = PHYSICS_CONFIG.projectileSpeed;
          
          const newProjectile = {
            id: Date.now(),
            x: slingshotData.x,
            y: slingshotData.y,
            targetX: targetX,
            targetY: targetY,
            dirX: dirX,
            dirY: dirY,
            speed: speed,
            radius: Math.max(6, Math.min(12, canvas.width * 0.012)), // 화면 크기에 비례한 발사체 크기
            trail: [], // 궤적 저장용 배열
            distanceTraveled: 0, // 이동한 거리
            totalDistance: distance // 총 이동해야 할 거리
          };
          
          // 발사 성공
          
          setProjectiles(prev => [...prev, newProjectile]);
          projectilesRef.current = [...projectilesRef.current, newProjectile];
        } else {
          // 발사 실패
        }
        
        // 드래그 상태 해제 및 조준점, 궤적 숨기기
        slingshotRef.current = { ...slingshotData, isDragging: false, pullDistance: 0 };
        setSlingshot(slingshotRef.current);
        crosshairRef.current = { x: 0, y: 0, visible: false };
        setCrosshair(crosshairRef.current);
        trajectoryDotsRef.current = [];
        setTrajectoryDots([]);
      }
    };

    // 통합된 터치/마우스 이벤트 핸들러
    const handlePointerDown = (evt) => {
      evt.preventDefault();
      evt.stopPropagation();
      handleMouseDown(evt);
    };

    const handlePointerMove = (evt) => {
      evt.preventDefault();
      evt.stopPropagation();
      handleMouseMove(evt);
    };

    const handlePointerUp = (evt) => {
      evt.preventDefault();
      evt.stopPropagation();
      handleMouseUp(evt);
    };

    // 이벤트 리스너 등록 - 터치와 마우스 모두 지원
    canvas.addEventListener('mousedown', handlePointerDown, { passive: false });
    canvas.addEventListener('mousemove', handlePointerMove, { passive: false });
    canvas.addEventListener('mouseup', handlePointerUp, { passive: false });
    
    // 터치 이벤트 (모바일)
    canvas.addEventListener('touchstart', handlePointerDown, { passive: false });
    canvas.addEventListener('touchmove', handlePointerMove, { passive: false });
    canvas.addEventListener('touchend', handlePointerUp, { passive: false });
    
    // 터치 취소 시에도 처리
    canvas.addEventListener('touchcancel', handlePointerUp, { passive: false });

    return () => {
      canvas.removeEventListener('mousedown', handlePointerDown);
      canvas.removeEventListener('mousemove', handlePointerMove);
      canvas.removeEventListener('mouseup', handlePointerUp);
      canvas.removeEventListener('touchstart', handlePointerDown);
      canvas.removeEventListener('touchmove', handlePointerMove);
      canvas.removeEventListener('touchend', handlePointerUp);
      canvas.removeEventListener('touchcancel', handlePointerUp);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [stagePhase, localStage, quiz?.questions, feedbackActive, feedbackIsSuccess]);

  // 물리 업데이트 (발사체 이동 및 충돌 감지)
  useEffect(() => {
    if (stagePhase !== 'play') return;

    const updatePhysics = () => {
      // 간단한 직선 이동 시스템을 사용한 발사체 업데이트
      setProjectiles(prev => {
        const updated = prev.map(projectile => {
          const newProjectile = { ...projectile };
          
          // 현재 위치를 트레일에 저장
          if (!newProjectile.trail) newProjectile.trail = [];
          newProjectile.trail.push({
            x: newProjectile.x,
            y: newProjectile.y
          });
          
          // 트레일 길이 제한 (최대 8개 점)
          if (newProjectile.trail.length > 8) {
            newProjectile.trail.shift();
          }
          
          // 직선 이동 업데이트
          const moveDistance = newProjectile.speed;
          newProjectile.distanceTraveled += moveDistance;
          
          // 목표지점에 도달했는지 확인
          if (newProjectile.distanceTraveled >= newProjectile.totalDistance) {
            // 목표지점에 정확히 도달
            newProjectile.x = newProjectile.targetX;
            newProjectile.y = newProjectile.targetY;
            newProjectile.reachedTarget = true;
          } else {
            // 목표지점 방향으로 이동
            newProjectile.x += newProjectile.dirX * moveDistance;
            newProjectile.y += newProjectile.dirY * moveDistance;
          }
          
          return newProjectile;
        }).filter(projectile => 
          !projectile.reachedTarget && // 목표에 도달하지 않은 발사체만 유지
          projectile.x > -100 && projectile.x < (canvasRef.current?.width || 800) + 100 &&
          projectile.y > -100 && projectile.y < (canvasRef.current?.height || 600) + 100
        );
        
        projectilesRef.current = updated;
        return updated;
      });

      // 목표 지점 도달 감지는 이미 위에서 처리됨 (reachedTarget 플래그)
      // 별도의 조준점 충돌 감지 불필요

      // 간단한 2D 충돌 감지
      const currentProjectiles = projectilesRef.current;
      const currentBalloons = balloonsRef.current;
      
      currentProjectiles.forEach(projectile => {
        currentBalloons.forEach(balloon => {
          if (balloon.popped) return;
          
          // 2D 거리 계산
          const distance = Math.hypot(
            projectile.x - balloon.x,
            projectile.y - balloon.y
          );
          
          // 충돌 반경 계산 (풍선 중심부만 - 크기의 30% + 발사체 반지름)
          const balloonHitRadius = balloon.size * 0.3; // 풍선 크기의 30%만 충돌 영역으로 사용
          const hitDistance = balloonHitRadius + projectile.radius;
          
          if (distance < hitDistance) {
            // 충돌 발생!
            setBalloons(prev => {
              const next = prev.map(b => 
                b.id === balloon.id ? { ...b, popped: true } : b
              );
              balloonsRef.current = next;
              return next;
            });
            
            // 발사체 제거
            setProjectiles(prev => {
              const filtered = prev.filter(p => p.id !== projectile.id);
              projectilesRef.current = filtered;
              return filtered;
            });
            
            // 게임 로직 처리
            if (isTutorialStage) {
              // 튜토리얼 단계: 풍선 터트리기 성공
              const cx = canvasRef.current?.width / 2 || 300;
              const cy = canvasRef.current?.height / 2 || 200;
              setFeedbackActive(true);
              setFeedbackIsSuccess(true);
              const base = Math.min(canvasRef.current?.width || 600, canvasRef.current?.height || 400) * 0.3;
              setFeedbackRect({ x: cx, y: cy, w: base, h: base });
              setStagePhase('feedback');
            } else {
              // 퀴즈 단계: 퀴즈 정답 체크
              if (!stageAnsweredRef.current) {
                const isCorrect = balloon.correct === true;
                setOptionMeta(m => ({ ...m, selectedIndex: balloon.optionIndex }));
                
                if (isCorrect) {
                  correctCountRef.current += 1;
                } else {
                  // 수정된 인덱스 계산으로 wrongIndexes 기록
                  let qIndexRaw;
                  if (includeTutorial) {
                    qIndexRaw = localStage - 5;
                  } else {
                    qIndexRaw = localStage - 1;
                  }
                  
                  if (qIndexRaw >= 0) {
                    const oneBased = qIndexRaw + 1;
                    if (!wrongIndexesRef.current.includes(oneBased)) {
                      wrongIndexesRef.current.push(oneBased);
                    }
                  }
                }
                
                // 답안 기록 - 수정된 인덱스 계산
                let qIndexRaw;
                if (includeTutorial) {
                  qIndexRaw = localStage - 5;
                } else {
                  qIndexRaw = localStage - 1;
                }
                
                if (qIndexRaw >= 0 && Array.isArray(answersRef.current)) {
                  answersRef.current[qIndexRaw] = balloon.optionIndex;
                }
                
                stageAnsweredRef.current = true;
                // 답안 선택 시 게임 진행 상태 해제
                if (setIsGameInProgress) setIsGameInProgress(false);
                
                const cx = canvasRef.current?.width / 2 || 300;
                const cy = canvasRef.current?.height / 2 || 200;
                setFeedbackActive(true);
                setFeedbackIsSuccess(isCorrect);
                const base = Math.min(canvasRef.current?.width || 600, canvasRef.current?.height || 400) * 0.3;
                setFeedbackRect({ x: cx, y: cy, w: base, h: base });
                setStagePhase('feedback');
              }
            }
          }
        });
      });
    };

    const physicsInterval = setInterval(updatePhysics, 1000/60); // 정확히 60fps = 16.67ms
    return () => clearInterval(physicsInterval);
  }, [stagePhase, localStage, quiz?.questions, correctCountRef, answersRef, wrongIndexesRef, stageAnsweredRef, setStagePhase, setFeedbackActive, setFeedbackIsSuccess, setFeedbackRect, isTutorialStage, includeTutorial]);

  return (
    <div style={{ 
      width: '100%', 
      height: '100%', 
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      {/* 게임 캔버스 */}
      <canvas 
        ref={canvasRef} 
        style={{ 
          width: '100%',
          height: '100%',
          touchAction: 'none', 
          WebkitTapHighlightColor: 'transparent', 
          pointerEvents: stagePhase === 'play' ? 'auto' : 'none',
          display: 'block',
          cursor: stagePhase === 'play' ? 'crosshair' : 'default'
        }} 
      />
      
    </div>
  );
};

export default ShortFormQuizBalloonPop;
